import * as React from "react";
import { Outlet, NavLink } from "react-router-dom";
import { Home, History, Users, TrendingUp, Settings as SettingsIcon, Plus, Camera, Search, Menu, X, Zap, Wallet, Eye, LogOut } from "lucide-react";
import { NeonCommandPalette } from "@/ui/v3/NeonCommandPalette";
import { NeonBottomNav } from "@/ui/v3/NeonBottomNav";
import { InstallPrompt } from "@/components/InstallPrompt";
import { useViewMode } from "@/lib/view-mode-context";
import { useAuth } from "@/lib/auth";
import "@/ui/v3/NeonGlobals.css";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts";
import { cn } from "@/lib/utils";

export function NeonShell() {
  useKeyboardShortcuts();
  const [scrolled, setScrolled] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const { isPublic, targetUserId } = useViewMode();
  const { user, signOut } = useAuth();

  const userInitials = React.useMemo(() => {
    if (!user) return '?';
    const meta = user.user_metadata;
    if (meta?.name) return (meta.name as string).charAt(0).toUpperCase();
    if (meta?.full_name) return (meta.full_name as string).charAt(0).toUpperCase();
    if (user.email) return user.email.charAt(0).toUpperCase();
    return '?';
  }, [user]);

  const getPath = (base: string) => {
    if (!isPublic) return base;
    if (base === '/') return `/public/${targetUserId}`;
    return `/public/${targetUserId}${base}`;
  };

  const nav = [
    { to: getPath("/"), label: "Home", icon: Home, end: true },
    { to: getPath("/sessions"), label: "Sessions", icon: History },
    { to: getPath("/players"), label: "Players", icon: Users },
    { to: getPath("/expenses"), label: "Expenses", icon: Wallet },
    { to: getPath("/statistics"), label: "Stats", icon: TrendingUp },
  ];

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openPalette = () => {
    window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", ctrlKey: true }));
  };

  return (
    <div className="neon-root">
      {/* ── Ambient Glows ───────────────────────────────────── */}
      <div className="fixed top-0 left-1/4 w-[600px] h-[600px] bg-neon-cyan/5 blur-[130px] rounded-full pointer-events-none -translate-y-1/2 -translate-x-1/2 z-0" />
      <div className="fixed bottom-0 right-1/4 w-[500px] h-[500px] bg-neon-rose/5 blur-[110px] rounded-full pointer-events-none translate-y-1/2 translate-x-1/2 z-0" />
      <div className="fixed top-1/3 right-0 w-[300px] h-[300px] bg-neon-emerald/3 blur-[90px] rounded-full pointer-events-none z-0" />

      {/* ── Top Bar ────────────────────────────────────────── */}
      <header className={cn(
        "sticky top-0 z-50 transition-all duration-300 border-b",
        scrolled 
          ? "bg-neon-bg/80 backdrop-blur-2xl border-white/10 py-2 shadow-2xl" 
          : "bg-transparent border-transparent py-4"
      )}>
        <div className="max-w-[1280px] mx-auto px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-8">
            {/* logo */}
            <NavLink to={getPath("/")} className="flex items-center gap-2.5 no-underline group">
              <div className="w-9 h-9 rounded-xl grad-cyan-emerald flex items-center justify-center shadow-lg shadow-neon-cyan/20 group-hover:scale-105 transition-transform">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-black text-lg tracking-tighter text-white">
                SSC<span className="text-neon-cyan group-hover:text-neon-emerald transition-colors">SCORE</span>
              </span>
            </NavLink>

            {/* desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === '/' || (isPublic && n.to === `/public/${targetUserId}`)}
                  className={({ isActive }) => cn(
                    "flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all",
                    isActive 
                      ? "bg-white/10 text-white shadow-inner shadow-neon-cyan/5" 
                      : "text-neon-muted hover:text-white hover:bg-white/5"
                  )}
                >
                  <n.icon className="w-3.5 h-3.5" />
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <InstallPrompt />
            
            {isPublic && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-neon-cyan/10 border border-neon-cyan/20 text-neon-cyan text-[10px] font-black uppercase tracking-widest">
                <Eye className="w-3 h-3" />
                Read Only
              </div>
            )}

            {!isPublic && (
              <div className="hidden md:flex items-center gap-2">
                <NavLink to="/session/new">
                  <button className="neon-btn-primary px-4 py-2 text-[10px] uppercase font-black flex items-center gap-2 rounded-xl">
                    <Plus className="w-3.5 h-3.5" /> New
                  </button>
                </NavLink>
                <button
                  onClick={openPalette}
                  className="p-2.5 rounded-xl bg-white/5 border border-white/5 text-neon-muted hover:text-white hover:border-white/20 transition-all"
                  title="Search (⌘K)"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* user avatar */}
            {!isPublic && user && (
              <div className="flex items-center gap-2 pl-2 border-l border-white/10">
                <div className="w-9 h-9 rounded-full bg-neon-cyan/10 border border-neon-cyan/30 flex items-center justify-center text-xs font-black text-neon-cyan shadow-[0_0_15px_hsl(var(--neon-cyan)/0.15)]">
                  {userInitials}
                </div>
                <button
                  onClick={() => signOut()}
                  className="hidden md:flex p-2 rounded-xl text-neon-muted hover:text-neon-crimson hover:bg-neon-crimson/5 transition-all"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* mobile menu toggle */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden p-2.5 rounded-xl bg-white/5 border border-white/5 text-white"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* mobile menu sheet */}
        {menuOpen && (
          <div className="md:hidden animate-slide-up px-4 pb-6 pt-2 flex flex-col gap-2">
            <div className="glass-card p-2 flex flex-col gap-1">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setMenuOpen(false)}
                  end={n.to === '/' || (isPublic && n.to === `/public/${targetUserId}`)}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all",
                    isActive ? "bg-neon-cyan/20 text-neon-cyan" : "text-neon-muted"
                  )}
                >
                  <n.icon className="w-4 h-4" />
                  {n.label}
                </NavLink>
              ))}
            </div>

            {!isPublic && (
              <div className="glass-card p-4 flex flex-col gap-3">
                <NavLink to="/session/new" onClick={() => setMenuOpen(false)}>
                  <button className="neon-btn-primary w-full py-3.5 rounded-xl font-black uppercase text-xs tracking-widest flex items-center justify-center gap-2 shadow-xl shadow-neon-cyan/20">
                    <Plus className="w-4 h-4" /> New Session
                  </button>
                </NavLink>
                <div className="grid grid-cols-2 gap-2">
                  <NavLink to="/session/import" onClick={() => setMenuOpen(false)}>
                    <button className="neon-btn-secondary w-full py-3 rounded-xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2">
                      <Camera className="w-3.5 h-3.5" /> Import
                    </button>
                  </NavLink>
                  <NavLink to="/settings" onClick={() => setMenuOpen(false)}>
                    <button className="neon-btn-secondary w-full py-3 rounded-xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2">
                      <SettingsIcon className="w-3.5 h-3.5" /> Settings
                    </button>
                  </NavLink>
                </div>
                {user && (
                  <button
                    onClick={() => { setMenuOpen(false); signOut(); }}
                    className="w-full py-3 rounded-xl bg-neon-crimson/5 border border-neon-crimson/10 text-neon-crimson font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2"
                  >
                    <LogOut className="w-3.5 h-3.5" /> Sign Out
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </header>

      {/* ── Page Content ───────────────────────────────────── */}
      <main className="neon-main relative z-10">
        <Outlet />
      </main>

      {!isPublic && <NeonCommandPalette />}
      <NeonBottomNav />
    </div>
  );
}
